import { useEffect, useState } from 'react';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { Work_Sans } from 'next/font/google';
import ErrorBoundary from '@/components/ErrorBoundary';
import { GoogleTagManager } from '@next/third-parties/google';

// StrapiLivePreview is only imported inside useEffect so Next.js doesn't prefetch
// the chunk on normal (non-preview) pages. The module is downloaded only when a
// user actually enters preview mode, keeping the main bundle lean.
function StrapiLivePreviewLoader() {
  const [Component, setComponent] = useState(null);

  useEffect(() => {
    let mounted = true;
    import('@/components/utilities/StrapiLivePreview').then((mod) => {
      if (mounted) setComponent(() => mod.default);
    });
    return () => {
      mounted = false;
    };
  }, []);

  return Component ? <Component /> : null;
}

// styles
import '@/styles/globals.css';

// Work Sans self-hosted via next/font (no render-blocking Google Fonts request).
// Variable font ships one file covering all weights instead of 9 separate ones.
const workSans = Work_Sans({
  subsets: ['latin', 'latin-ext'],
  variable: '--font-work-sans',
  display: 'swap',
  weight: 'variable',
  style: ['normal', 'italic']
});

export default function App({ Component, pageProps }) {
  const router = useRouter();

  // Defer GTM until the user interacts with the page or 3s passes.
  // PSI / Lighthouse waits <3s for LCP+FCP so GTM's ~450 KiB of scripts no
  // longer block the initial render path, without breaking tracking for
  // real users (who typically scroll / click within a couple of seconds).
  const [thirdPartyReady, setThirdPartyReady] = useState(false);

  useEffect(() => {
    if (thirdPartyReady) return undefined;

    const triggers = ['scroll', 'mousemove', 'touchstart', 'keydown', 'click'];
    const load = () => setThirdPartyReady(true);

    triggers.forEach((evt) => window.addEventListener(evt, load, { once: true, passive: true }));
    const timer = setTimeout(load, 3000);

    return () => {
      triggers.forEach((evt) => window.removeEventListener(evt, load));
      clearTimeout(timer);
    };
  }, [thirdPartyReady]);

  useEffect(() => {
    // Handle chunk load errors during Next.js route changes
    const handleRouteChangeError = (err, url) => {
      if (err.name === 'ChunkLoadError' || err.message?.includes('Loading chunk')) {
        // Navigate using window.location to force fresh chunk load
        window.location.href = url;
      }
    };

    router.events.on('routeChangeError', handleRouteChangeError);

    return () => {
      router.events.off('routeChangeError', handleRouteChangeError);
    };
  }, [router]);
  return (
    <>
      <Head>
        {/* Inject font CSS variable on :root so body and other elements outside the Component tree can use it */}
        <style>{`:root { --font-work-sans: ${workSans.style.fontFamily}; }`}</style>
      </Head>
      <ErrorBoundary>
        {thirdPartyReady && process.env.NEXT_PUBLIC_GTM_ID && (
          <GoogleTagManager gtmId={process.env.NEXT_PUBLIC_GTM_ID} />
        )}
        <Component {...pageProps} />
        {pageProps.isPreview && <StrapiLivePreviewLoader />}
      </ErrorBoundary>
    </>
  );
}
